import { useSelector } from 'store/hooks';
import { issuerSelector } from './selectors';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { compact } from 'lodash';
import { AdminApi, AdminProductGlobalOverview, AdminIncompleteInvestor, DropdownElement } from 'api';
import { useApiCall } from 'hooks/use-api-call';
import {
  hasManageApprovalsPermission,
  hasManageCreditDigitalSecuritiesPermission,
  hasManageIncomingPaymentsPermission,
} from 'src/apps/issuer/helpers/tokenPermissions';
import { debounce } from 'lodash';
import { useCurrentUserSelector } from 'src/core/auth/hooks';

export function useIssuerSelector() {
  return useSelector(issuerSelector);
}

export const useProductsFilter = ({ forceDataLoad }: { forceDataLoad?: boolean }) => {
  const [products, setProducts] = useState<DropdownElement[]>([]);
  const { withApi, makeAuthenticatedApi } = useApiCall();
  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const productsFilter = useMemo(
    () =>
      products.map((product) => ({
        value: product.id,
        label: product.name,
        name: product.name,
      })),
    [products],
  );

  const fetchProducts = useCallback(
    (searchQuery: string = '') => {
      withApi(async () => {
        const response = await adminApi.adminProductsList({ search: searchQuery, limit: 50 });
        setProducts(response.results || []);
      });
    },
    [withApi, adminApi],
  );

  const searchProductValue = useCallback(
    (value: string) => {
      fetchProducts(value);
    },
    [fetchProducts],
  );

  const debouncedProductSearch = useMemo(() => debounce(searchProductValue, 500), [searchProductValue]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    forceDataLoad && fetchProducts();
  }, [forceDataLoad]);

  return { productsFilter, debouncedProductSearch };
};

interface RequiredIssuerAction {
  type: 'approveOffers' | 'confirmPayments' | 'creditSecurities';
  number: number;
  link: string;
}

export function useIssuerOverview() {
  const { withApi, makeAuthenticatedApi, loading } = useApiCall(true);
  const { currentUser } = useCurrentUserSelector();

  const [productsOverview, setProductsOverview] = useState<AdminProductGlobalOverview>();
  const [investorsIncompleteList, setInvestorsIncompleteList] = useState<AdminIncompleteInvestor[]>();

  const api: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const canApprove = hasManageApprovalsPermission(currentUser);
  const canManagePayments = hasManageIncomingPaymentsPermission(currentUser);
  const canCreditDigitalSecurities = hasManageCreditDigitalSecuritiesPermission(currentUser);

  const requiredActions: RequiredIssuerAction[] | undefined = useMemo(() => {
    if (!productsOverview) return;
    const {
      numberApprovableInvestments,
      numberPaymentsOutstandingInvestments,
      numberSecuritiesOutstandingInvestments,
    } = productsOverview;

    return compact([
      numberApprovableInvestments &&
        canApprove && {
          type: 'approveOffers',
          number: numberApprovableInvestments,
          link: 'approveInvestments',
        },
      numberPaymentsOutstandingInvestments &&
        canManagePayments && {
          type: 'confirmPayments',
          number: numberPaymentsOutstandingInvestments,
          link: 'confirmPayments',
        },
      numberSecuritiesOutstandingInvestments &&
        canCreditDigitalSecurities && {
          type: 'creditSecurities',
          number: numberSecuritiesOutstandingInvestments,
          link: 'creditAssets',
        },
    ]);
  }, [
    productsOverview?.numberApprovableInvestments,
    productsOverview?.numberPaymentsOutstandingInvestments,
    productsOverview?.numberSecuritiesOutstandingInvestments,
    canApprove,
    canManagePayments,
    canCreditDigitalSecurities,
  ]);

  useEffect(() => {
    withApi(async () => {
      const globalOverview = await api.adminOverviewRetrieve();
      setProductsOverview(globalOverview);

      const investorsList = await api.adminInvestorsIncompleteList({});
      setInvestorsIncompleteList(investorsList);
    });
  }, [withApi, api]);

  return {
    requiredActions,
    productsOverview,
    investorsIncompleteList,
    loading,
  };
}
